import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import DateTimeField, { toLocalInput, presetPlusDays, presetEndOfMonth } from '@/components/ui/DateTimeField';
import { COUPON_TYPES, COUPON_TYPE_LABELS, optsFrom } from '@/lib/enums';
import { maskMoney, moneyToNumber, numberToMoney } from '@/lib/masks';

const empty = {
  code: '',
  type: 'percent',
  value: '',
  maxRedemptions: '',
  expiresAt: '',
  active: true,
};

// Converte 'YYYY-MM-DDTHH:mm' do input para ISO completo (com Z).
const toIso = (local) => (local ? new Date(local).toISOString() : null);
// ISO → input no fuso LOCAL (toISOString deslocaria a hora exibida).
const toLocal = (iso) => (iso ? toLocalInput(iso) : '');

/** Form de cupom (plataforma quando trainerId nulo). */
export default function CouponForm({ initial, onSubmit, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ...empty,
    ...initial,
    // Valor fixo é editado com máscara de dinheiro; percentual fica numérico.
    value: initial?.type === 'fixed' ? numberToMoney(initial?.value) : initial?.value ?? '',
    maxRedemptions: initial?.maxRedemptions ?? '',
    expiresAt: toLocal(initial?.expiresAt),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return setErr(t('coupons.errCodeRequired'));
    const value = form.type === 'fixed' ? moneyToNumber(form.value) : Number(form.value);
    if (!value || value <= 0) return setErr(t('coupons.errValueInvalid'));
    setSaving(true);
    setErr(null);
    try {
      // Monta payload só com o que a API espera (campos numéricos/datas tratados).
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value,
        active: !!form.active,
      };
      if (form.maxRedemptions !== '' && form.maxRedemptions != null) {
        payload.maxRedemptions = Number(form.maxRedemptions);
      }
      if (form.expiresAt) payload.expiresAt = toIso(form.expiresAt);
      await onSubmit(payload);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <Input label={t('coupons.fieldCode')} value={form.code} onChange={set('code')} placeholder="MOVEZ10" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Select label={t('coupons.fieldType')} value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, value: '' }))}
          options={optsFrom(COUPON_TYPES, COUPON_TYPE_LABELS)} />
        {form.type === 'fixed' ? (
          <Input label={t('coupons.fieldValue')} inputMode="numeric" value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: maskMoney(e.target.value) }))} placeholder="20,00" />
        ) : (
          <Input label={t('coupons.fieldValue')} type="number" min="0" max="100" step="0.01" value={form.value}
            onChange={set('value')} placeholder="10" />
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Input label={t('coupons.fieldMaxUses')} type="number" min="1" step="1" value={form.maxRedemptions}
          onChange={set('maxRedemptions')} placeholder={t('coupons.placeholderUnlimited')} />
        <DateTimeField
          label={t('coupons.fieldExpiresAt')}
          value={form.expiresAt}
          onChange={(v) => setForm((f) => ({ ...f, expiresAt: v }))}
          defaultTime="23:59"
          clearable
          presets={[
            { label: t('datetime.presetPlus7d'), value: presetPlusDays(7, { endOfDay: true }) },
            { label: t('datetime.presetPlus30d'), value: presetPlusDays(30, { endOfDay: true }) },
            { label: t('datetime.presetEndOfMonth'), value: presetEndOfMonth },
          ]}
        />
      </div>
      <Select label={t('screens.status')} value={form.active ? '1' : '0'}
        onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === '1' }))}
        options={[{ value: '1', label: t('coupons.statusActive') }, { value: '0', label: t('coupons.statusInactive') }]} />
      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={saving}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
